import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Sparkles, CheckCircle2, KeyRound, ShieldCheck, Ticket, CalendarCheck, Wrench, Car, PiggyBank, Tv, Briefcase, ChevronRight, Clock, Info } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth, API } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { useState } from "react";
import { currency } from "@/lib/format";

const benefits = [
  { icon: Clock, t: "Hourly & short-term rentals (1–6 days)", d: "Members-only access to hourly billing (minimum 1 hour) and daily rentals of 1 to 6 days. Non-members must book at least 7 days — but as a member you can grab a vehicle for a few hours, a weekend getaway, or a mid-week errand. Includes special member pricing (approximately 10% off the standard hourly rate)." },
  { icon: KeyRound, t: "Access to Rental Term Leasing", d: "Reserve premium vehicles for longer, fixed terms of 3, 6, or 12 months. Deposit-based approval process. Approval is based on identity verification, membership status, deposit, insurance eligibility, signed terms, and vehicle availability." },
  { icon: Car, t: "Premium vehicle access", d: "Access modern vehicles without the long-term commitment of ownership. Clear terms, flexible durations, defined end dates." },
  { icon: Wrench, t: "Maintenance options (12-month Rental Term Leasing)", d: "Choose AlfaDrive-Managed (approximately +$160/month) or Member-Managed and save up to 25% on the monthly rate. All maintenance follows AlfaDrive standards." },
  { icon: PiggyBank, t: "Member rental discounts", d: "Exclusive member pricing on eligible rentals. Save up to hundreds of dollars per booking depending on the vehicle, duration, and coverage selected." },
  { icon: CalendarCheck, t: "Two free rental days per month", d: "Book 4+ eligible rental days in a month and receive 2 free rental days. Taxes, protection, deposits, and applicable charges may still apply." },
  { icon: ShieldCheck, t: "Insurance and protection options", d: "Use AlfaDrive protection (including Collision Damage Waiver where applicable) or provide eligible personal insurance for review and approval." },
  { icon: ShieldCheck, t: "One-year CAA membership included", d: "We cover 1 year of CAA membership — roadside support and extra peace of mind included with your annual membership." },
  { icon: Ticket, t: "Exclusive events and giveaways", d: "Event discounts, ticket giveaways, travel and stay offers, seasonal promotions, and partner perks throughout the year." },
  { icon: Tv, t: "IPTV bonus", d: "1 year of IPTV access included with your annual membership — an added lifestyle perk." },
];

export default function DriveAlfaClubPage() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);

  const join = async () => {
    if (!user) { window.location.href = "/login?next=/drive-alfa-club"; return; }
    if (user.is_member) { toast.info("You are already a Drive Alfa Club member."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/membership/join`, { payment_method: "card_placeholder" });
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
      localStorage.setItem("ad_user", JSON.stringify(me.data));
      toast.success("Welcome to Drive Alfa Club!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join membership");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="club-page">
      <section className="hero-wash border-b border-border">
        <div className="container-x py-12 sm:py-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Consumer membership program
              </div>
              <h1 className="mt-4 font-display text-4xl sm:text-6xl font-bold leading-[1.02] tracking-tight">
                Drive Alfa Club
              </h1>
              <p className="mt-4 font-display text-2xl sm:text-3xl text-[hsl(var(--primary))]">Hourly rentals. Rental Term Leasing. Real perks.</p>
              <p className="mt-4 text-muted-foreground max-w-2xl">
                An annual consumer membership that unlocks hourly and short-term (1–6 day) rentals, member-only rental pricing, access to Rental Term Leasing (3, 6, or 12-month terms), a full year of CAA membership, and exclusive event perks.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button onClick={join} disabled={busy} className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="drive-alfa-club-join-button">
                  {user?.is_member ? "You are a member" : busy ? "Processing…" : "Join Drive Alfa Club"}
                </Button>
                <Button asChild variant="outline" className="h-12 px-6 rounded-xl">
                  <a href="#benefits">See benefits <ChevronRight className="h-4 w-4 ml-1" /></a>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="card-clean p-7" data-testid="membership-pricing-card">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Annual membership</div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="font-display text-5xl font-semibold tabular-nums">{currency(1000)}</span>
                  <span className="text-sm text-muted-foreground">/ year</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 badge-blue rounded-full px-3 py-1 text-xs">
                  <Sparkles className="h-3 w-3" /> Member pricing on eligible rentals
                </div>
                <ul className="mt-5 space-y-2">
                  {[
                    "Hourly & 1–6 day short-term rentals (members only)",
                    "Access to Rental Term Leasing (3, 6, 12 months)",
                    "Member pricing on eligible rentals",
                    "2 free rental days per month",
                    "1-year CAA membership included",
                    "Priority booking and holds",
                    "Event and lifestyle perks + IPTV",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /> {b}</li>
                  ))}
                </ul>
                <Button onClick={join} disabled={busy} className="w-full mt-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="membership-join-button">
                  {user?.is_member ? "You are a member" : busy ? "Processing…" : "Join Drive Alfa Club"}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center mt-2">Approval is streamlined and secure. Payment collection is currently a placeholder for MVP.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="benefits" className="section-y bg-white">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Core benefits</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-1">Every benefit — spelled out</h2>
            <p className="text-muted-foreground mt-3">A consumer membership designed to give you exceptional value from day one.</p>
          </div>

          <div className="mt-8 hidden lg:grid grid-cols-2 gap-5">
            {benefits.map((b, i) => (
              <div key={i} className="card-clean p-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[hsl(var(--blue-100))] flex items-center justify-center"><b.icon className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
                  <div className="font-display text-lg font-semibold">{b.t}</div>
                </div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>

          <Accordion type="single" collapsible className="mt-6 lg:hidden">
            {benefits.map((b, i) => (
              <AccordionItem key={i} value={`b-${i}`}>
                <AccordionTrigger className="text-left">
                  <span className="flex items-center gap-2"><b.icon className="h-4 w-4 text-[hsl(var(--primary))]" />{b.t}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{b.d}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section id="rental-term-leasing" className="section-y bg-[hsl(var(--charcoal-100))]">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Rental Term Leasing</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-1">A flexible alternative to ownership</h2>
            <p className="text-muted-foreground mt-3">
              With Rental Term Leasing, eligible Drive Alfa Club members can access longer-term vehicle plans through a deposit-based approval process. No traditional credit check or employment check required. Approval is based on identity verification, membership status, deposit, insurance/protection eligibility, signed rental terms, and vehicle availability. The longer the term, the lower the monthly rate can become.
            </p>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {[
              {
                k: "3 months",
                ex: "$2,300/month",
                d: "A flexible short-term option for drivers who want premium vehicle access without a long commitment. Perfect for seasonal needs, temporary transportation, business use, or trying a vehicle before going longer.",
              },
              {
                k: "6 months",
                ex: "$2,000/month",
                d: "A stronger monthly value with more time behind the wheel. Ideal for customers who want longer-term flexibility, lower monthly pricing, and access to a premium vehicle without traditional lease barriers.",
              },
              {
                k: "12 months",
                ex: "$1,400/month",
                d: "The best monthly value for members who want consistent vehicle access for the year. Drive longer, save more, and enjoy a premium AlfaDrive vehicle with a defined end date and flexible support.",
              },
            ].map((p) => (
              <div key={p.k} className="card-clean p-6 flex flex-col" data-testid={`rtl-plan-${p.k.split(" ")[0]}`}>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Rental Term Leasing plan</div>
                <div className="mt-1 font-display text-2xl font-semibold">{p.k}</div>
                <div className="mt-2 text-sm text-muted-foreground">From as low as <span className="font-semibold text-foreground">{p.ex}</span></div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.d}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs badge-blue rounded-full px-3 py-1 self-start"><Sparkles className="h-3 w-3" /> Member-only</div>
                <div className="mt-5"><Button asChild className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]"><Link to="/vehicles">Choose a vehicle</Link></Button></div>
              </div>
            ))}
          </div>

          <div
            className="mt-6 flex items-start gap-2.5 text-xs text-muted-foreground max-w-3xl"
            data-testid="rtl-pricing-disclaimer"
          >
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
            <p className="leading-relaxed">
              Pricing reflects premium vehicle examples. Economy and business-class options may be available at lower rates. Final pricing varies by vehicle, availability, selected term, deposit, protection options, and member eligibility.
            </p>
          </div>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <Briefcase className="h-6 w-6 text-[hsl(var(--primary))]" />
            <h2 className="mt-3 font-display text-3xl font-semibold">Need vehicles for your business?</h2>
            <p className="mt-3 text-muted-foreground">Alfa One Path is our separate business/fleet program. It’s designed for companies, contractors, rideshare operators, and corporate accounts — not part of consumer membership.</p>
            <div className="mt-5"><Button asChild className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]"><Link to="/alfa-one-path">Explore Alfa One Path (business)</Link></Button></div>
          </div>
          <div className="lg:col-span-5">
            <div className="card-clean p-6">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Future vehicle ownership</div>
              <div className="mt-2 font-display text-xl font-semibold">After eligible Rental Term Leasing periods</div>
              <p className="text-sm text-muted-foreground mt-2">Select members may be invited to explore purchase, finance, or lease-to-own opportunities on their favourite AlfaDrive vehicles — subject to approval, availability, and applicable terms.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x">
          <div className="rounded-3xl card-clean hero-wash p-10 sm:p-14 relative overflow-hidden text-center">
            <Sparkles className="h-6 w-6 text-[hsl(var(--primary))] mx-auto" />
            <h3 className="mt-3 font-display text-3xl sm:text-4xl font-semibold">Ready to unlock member benefits?</h3>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Join Drive Alfa Club and start saving on every rental, plus unlock Rental Term Leasing options.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Button onClick={join} disabled={busy} className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="club-join-bottom">
                {user?.is_member ? "You are a member" : busy ? "Processing…" : "Join Drive Alfa Club · $1,000/yr"}
              </Button>
              <Button asChild variant="outline" className="h-12 px-6 rounded-xl"><Link to="/vehicles">Browse fleet</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
